import React, { useState, useMemo } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getBuySells, deleteBuySell } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import {
  EnhancedPageLayout,
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
} from "../../components/ui/enhanced-page-layout";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import {
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  DollarSign,
  Grid3X3,
  List,
  Table,
} from "lucide-react";

export default function BuySellPage() {
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedLocation } = useLocationContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "title" | "price">(
    "newest",
  );

  // Use integrated responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("cards");

  useRequireSectionVisible("BUY_SELL");

  const {
    data: items = [],
    isLoading,
    error,
  } = useQuery(getBuySells, {
    locationIds: selectedLocation?.id ? [selectedLocation.id] : [],
  });

  const filteredItems = useMemo(() => {
    let result = [...items];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          item.title.toLowerCase().includes(term) ||
          item.description.toLowerCase().includes(term),
      );
    }

    if (selectedCategory !== "all") {
      result = result.filter((item) => item.category === selectedCategory);
    }

    result.sort((a, b) => {
      if (sortBy === "newest")
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      if (sortBy === "oldest")
        return (
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      if (sortBy === "title") return a.title.localeCompare(b.title);
      if (sortBy === "price") return (b.price || 0) - (a.price || 0);
      return 0;
    });

    return result;
  }, [items, searchTerm, selectedCategory, sortBy]);

  const getConditionColor = (condition: string) => {
    const colors = {
      NEW: "bg-green-100 text-green-800 border-green-200",
      LIKE_NEW: "bg-blue-100 text-blue-800 border-blue-200",
      GOOD: "bg-yellow-100 text-yellow-800 border-yellow-200",
      FAIR: "bg-orange-100 text-orange-800 border-orange-200",
      POOR: "bg-red-100 text-red-800 border-red-200",
    };
    return (
      colors[condition as keyof typeof colors] ||
      "bg-muted text-muted-foreground border border-border"
    );
  };

  const headerContent = (
    <div className="px-4 py-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Buy & Sell</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
            Items in {selectedLocation?.name || "Location"} • {filteredItems.length} items
          </p>
        </div>
        <div className="flex items-center gap-3">
          <EnhancedActionButton
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            icon={Filter}
            label=""
            variant={isFilterExpanded ? "primary" : "secondary"}
            className="hidden lg:flex"
          />

          {/* Mobile Filter Button */}
          <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
            <EnhancedActionButton
              onClick={() => setIsFilterExpanded(true)}
              icon={Filter}
              label=""
              variant={isFilterExpanded ? "primary" : "secondary"}
              className="lg:hidden"
            />
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="py-6 space-y-6">
                {/* Mobile Search */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Categories</option>
                    <option value="BUY">Buying</option>
                    <option value="SELL">Selling</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="title">Title A-Z</option>
                    <option value="price">Price (High-Low)</option>
                  </select>
                </div>

                <Button 
                  className="w-full" 
                  onClick={() => setIsFilterExpanded(false)}
                >
                  Apply Filters
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          {user?.isAdmin && (
            <EnhancedActionButton
              onClick={() => {
                if (selectedLocation?.locationCode) {
                  navigate(
                    `/locations/${selectedLocation.locationCode}/buy-sell/add`,
                  );
                } else {
                  navigate("/buy-sell/add");
                }
              }}
              icon={Plus}
              label={<span className="hidden sm:inline">Post Item</span>}
            />
          )}
        </div>
      </div>
    </div>
  );

  const filtersContent = (
    <div 
      className={cn(
        "bg-sidebar-hover border-t border-border transition-all duration-300 overflow-hidden hidden lg:block",
        isFilterExpanded ? "max-h-[500px]" : "max-h-0 border-none"
      )}
    >
      <div className="px-6 py-3 space-y-3">
        {/* Column Headers - Only for table view */}
        {viewMode === "table" && (
          <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
            <div className="col-span-3 min-w-0 truncate">Item</div>
            <div className="col-span-1 min-w-0 truncate">Category</div>
            <div className="col-span-1 min-w-0 truncate">Price</div>
            <div className="col-span-2 min-w-0 truncate">Condition</div>
            <div className={user?.isAdmin ? "col-span-1" : "col-span-2"}>Author</div>
            <div className="col-span-2 min-w-0 truncate">Date</div>
            {user?.isAdmin && <div className="col-span-2 text-right">Actions</div>}
          </div>
        )}
        
        {/* Column Filters */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="col-span-1">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All</option>
              <option value="BUY">Buying</option>
              <option value="SELL">Selling</option>
            </select>
          </div>
          <div className="col-span-1">
            <select
              value={sortBy === "price" ? "price" : "all"}
              onChange={(e) =>
                setSortBy(e.target.value === "price" ? "price" : "newest")
              }
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Any Price</option>
              <option value="price">High-Low</option>
            </select>
          </div>
          <div className="col-span-2">
            {/* Condition filter can be added here if needed */}
          </div>
          <div className="col-span-2">
            {/* Author filter can be added here if needed */}
          </div>
          <div className="col-span-3">
            <select
              value={
                sortBy === "newest" || sortBy === "oldest"
                  ? sortBy
                  : "newest"
              }
              onChange={(e) => setSortBy(e.target.value as any)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title">A-Z</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <EnhancedPageLayout
        header={headerContent}
        filters={filtersContent}
      >
        <div className="w-full">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading items...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <DollarSign className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Error Loading Items
                </h3>
                <p className="text-gray-600">Please try again later.</p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && filteredItems.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Items Found
                </h3>
                <p className="text-gray-600">
                  {searchTerm || selectedCategory !== "all"
                    ? "Try adjusting your search or filters to find what you're looking for."
                    : "Be the first to post an item in this location!"}
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          {!isLoading && !error && filteredItems.length > 0 && (
            <>
              {/* Cards View */}
              {viewMode === "cards" && (
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredItems.map((item) => (
                      <div
                        key={item.id}
                        className={`${enhancedPageLayoutStyles.card.container} cursor-pointer hover:shadow-md transition-all`}
                        onClick={() => navigate(`/buy-sell/${item.id}`)}
                      >
                        <div className={enhancedPageLayoutStyles.card.content}>
                          <div className={enhancedPageLayoutStyles.card.header}>
                            <div className="flex items-center gap-2">
                              <span
                                className={cn(
                                  "px-2 py-0.5 rounded text-xs font-medium",
                                  item.category === "BUY"
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-green-100 text-green-800",
                                )}
                              >
                                {item.category}
                              </span>
                              <span
                                className={cn(
                                  "px-2 py-0.5 rounded text-xs font-medium border",
                                  getConditionColor(item.condition),
                                )}
                              >
                                {item.condition?.replace("_", " ") || "New"}
                              </span>
                            </div>
                            <span className="text-lg font-bold text-blue-600">
                              {item.price ? `$${item.price}` : "Free"}
                            </span>
                          </div>
                          <h3 className={enhancedPageLayoutStyles.card.title}>
                            {item.title}
                          </h3>
                          <p
                            className={
                              enhancedPageLayoutStyles.card.description
                            }
                          >
                            {item.description}
                          </p>
                          <div className={enhancedPageLayoutStyles.card.footer}>
                            <div className={enhancedPageLayoutStyles.card.meta}>
                              <div className="flex items-center gap-1">
                                <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold">
                                  {item.author.username?.[0]?.toUpperCase() ||
                                    "U"}
                                </div>
                                <span className="truncate max-w-[80px]">
                                  {item.author.username || "User"}
                                </span>
                              </div>
                              <span>•</span>
                              <span>
                                {new Date(
                                  item.createdAt,
                                ).toLocaleDateString()}
                              </span>
                            </div>
                            <EnhancedIconButton
                              onClick={(e) => {
                                e?.stopPropagation();
                                navigate(`/buy-sell/${item.id}`);
                              }}
                              icon={Eye}
                              title="View Details"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* List View */}
              {viewMode === "list" && (
                <div className="p-6 space-y-4">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className={enhancedPageLayoutStyles.list.item}
                      onClick={() => navigate(`/buy-sell/${item.id}`)}
                    >
                      <div className={enhancedPageLayoutStyles.list.content}>
                        <div className={enhancedPageLayoutStyles.list.left}>
                          <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                            <DollarSign className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {item.title}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span
                                className={cn(
                                  "px-2 py-0.5 rounded text-[10px] font-medium",
                                  item.category === "BUY"
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-green-100 text-green-800",
                                )}
                              >
                                {item.category}
                              </span>
                              <span className="text-xs text-gray-500">
                                {item.author.username || "User"} •{" "}
                                {new Date(item.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className={enhancedPageLayoutStyles.list.right}>
                          <span className="text-lg font-bold text-blue-600">
                            {item.price ? `$${item.price}` : "Free"}
                          </span>
                          <EnhancedIconButton
                            onClick={(e) => {
                              e?.stopPropagation();
                              navigate(`/buy-sell/${item.id}`);
                            }}
                            icon={Eye}
                            title="View Details"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Table View */}
              {viewMode === "table" && (
                <div className="w-full">
                  <div className={enhancedPageLayoutStyles.table.container}>
                    <div className={enhancedPageLayoutStyles.table.wrapper}>
                      <table className={enhancedPageLayoutStyles.table.table}>
                        <tbody
                          className={enhancedPageLayoutStyles.table.tbody}
                        >
                          {filteredItems.map((item) => (
                            <tr
                              key={item.id}
                              className="group hover:bg-sidebar-hover transition-colors cursor-pointer"
                              onClick={() => navigate(`/buy-sell/${item.id}`)}
                            >
                              <td
                                className={cn(
                                  enhancedPageLayoutStyles.table.td,
                                  "col-span-3",
                                )}
                              >
                                <div className="px-6 py-4 flex items-center gap-3">
                                  <div className="w-8 h-8 bg-blue-50 rounded flex items-center justify-center flex-shrink-0">
                                    <DollarSign className="w-4 h-4 text-blue-600" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="font-medium text-gray-900 truncate">
                                      {item.title}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">
                                      {item.description}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td
                                className={cn(
                                  enhancedPageLayoutStyles.table.td,
                                  "col-span-1",
                                )}
                              >
                                <div className="px-6 py-4">
                                  <span
                                    className={cn(
                                      "px-2 py-1 rounded-full text-[10px] font-medium",
                                      item.category === "BUY"
                                        ? "bg-blue-100 text-blue-800"
                                        : "bg-green-100 text-green-800",
                                    )}
                                  >
                                    {item.category}
                                  </span>
                                </div>
                              </td>
                              <td
                                className={cn(
                                  enhancedPageLayoutStyles.table.td,
                                  "col-span-1",
                                )}
                              >
                                <div className="px-6 py-4 font-bold text-blue-600">
                                  {item.price ? `$${item.price}` : "Free"}
                                </div>
                              </td>
                              <td
                                className={cn(
                                  enhancedPageLayoutStyles.table.td,
                                  "col-span-2",
                                )}
                              >
                                <div className="px-6 py-4">
                                  <span
                                    className={cn(
                                      "px-2 py-1 rounded-full text-[10px] font-medium border",
                                      getConditionColor(item.condition),
                                    )}
                                  >
                                    {item.condition?.replace("_", " ") || "New"}
                                  </span>
                                </div>
                              </td>
                              <td
                                className={cn(
                                  enhancedPageLayoutStyles.table.td,
                                  user?.isAdmin ? "col-span-1" : "col-span-2",
                                )}
                              >
                                <div className="px-6 py-4 text-sm text-gray-600">
                                  {item.author.username || "User"}
                                </div>
                              </td>
                              <td
                                className={cn(
                                  enhancedPageLayoutStyles.table.td,
                                  "col-span-2",
                                )}
                              >
                                <div className="px-6 py-4 text-sm text-gray-500">
                                  {new Date(
                                    item.createdAt,
                                  ).toLocaleDateString()}
                                </div>
                              </td>
                              {user?.isAdmin && (
                                <td
                                  className={cn(
                                    enhancedPageLayoutStyles.table.td,
                                    "col-span-2",
                                  )}
                                >
                                  <div className="px-6 py-4 flex justify-end gap-2">
                                    <EnhancedIconButton
                                      onClick={(e) => {
                                        e?.stopPropagation();
                                        navigate(`/buy-sell/${item.id}/edit`);
                                      }}
                                      icon={Edit}
                                      title="Edit"
                                    />
                                    <EnhancedIconButton
                                      onClick={(e) => {
                                        e?.stopPropagation();
                                        if (
                                          confirm(
                                            "Are you sure you want to delete this item?",
                                          )
                                        ) {
                                          deleteBuySell({
                                            itemId: item.id,
                                          });
                                          // Note: In a real app we'd refetch or use optimistic UI
                                        }
                                      }}
                                      icon={Trash2}
                                      title="Delete"
                                      variant="danger"
                                    />
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* Mobile version of table items */}
                  <div className="lg:hidden p-4 space-y-4">
                    {filteredItems.map((item) => (
                      <div
                        key={item.id}
                        className="bg-white border border-gray-200 rounded-lg p-4"
                        onClick={() => navigate(`/buy-sell/${item.id}`)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-bold text-gray-900 leading-tight">
                            {item.title}
                          </h3>
                          <span className="font-bold text-blue-600 whitespace-nowrap ml-2">
                            {item.price ? `$${item.price}` : "Free"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <span
                            className={cn(
                              "px-2 py-0.5 rounded text-[10px] font-medium",
                              item.category === "BUY"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800",
                            )}
                          >
                            {item.category}
                          </span>
                          <span className="text-[10px] text-gray-500">
                            By {item.author.username || "User"}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                          {item.description}
                        </p>
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/buy-sell/${item.id}`);
                            }}
                          >
                            Details
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </EnhancedPageLayout>
    </DashboardLayout>
  );
}
